import React from 'react';
import moment from 'moment';
import { useMutation } from '@apollo/client';
import {
  MutationRemoveCardFromWorkerArgs,
  Success,
  AppErrorCode,
  useAddCardToWorkerMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { getGraphQLError, getGraphQLErrorByCode } from 'utils/error';
import { REMOVE_CARD_FROM_WORKER } from 'containers/worker/helpers/queries';
import { useProximityCardFormatOptions } from 'containers/worker/helpers/useProximityCardFormatOptions';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { CheckIcon } from 'components/icons';
import { useIsMounted } from 'utils';
import { AlreadyExistingBadgeMessage } from 'components/alreadyExistingBadgeMessage';
import { ReactivateBadgeState } from 'containers/worker/modals/badgeModal/types';
import {
  WorkerCard,
  ReassignBadgeState,
  WorkerCardAlreadyExistsErrorExtensions,
} from 'containers/worker/modals/badgeModal/components/types';
import {
  getFormDefaultValues,
  getFormInputsHook,
} from './AssignBadgeModalContent.forms';
import { AssignBadgeFormData } from './types';

const instructions =
  'Enter the badge number printed on the back of the card near the bottom edge.';

export interface AssignBadgeModalContentProps {
  setOpen: () => void;
  onAction: (reassignBadgeState?: ReassignBadgeState) => void;
  jobsiteId: string;
  workerId: string;
  workerCards: WorkerCard[];
  reactivateBadge: (reactivateBadgeState: ReactivateBadgeState) => void;
}

export function AssignBadgeModalContent(
  props: AssignBadgeModalContentProps,
): React.ReactElement {
  const {
    setOpen,
    onAction,
    reactivateBadge,
    jobsiteId,
    workerId,
    workerCards,
  } = props;

  const isMounted = useIsMounted();
  const [isFetching, setIsFetching] = React.useState<boolean>(false);

  const [addCardToWorker] = useAddCardToWorkerMutation();

  const [removeCardFromWorker] = useMutation<
    Success,
    MutationRemoveCardFromWorkerArgs
  >(REMOVE_CARD_FROM_WORKER, {
    onError: (error) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
      setIsFetching(false);
    },
  });

  const onSubmit: FormOnSubmit<AssignBadgeFormData> = async (
    data,
    event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (isFetching) return;
    setIsFetching(true);

    const { badgeNumber, proximityCardFormatId, badges } = data;

    const addCardToWorkerAndRemoveExisting = async (options?: {
      forceCreate?: boolean;
    }): Promise<void> => {
      await addCardToWorker({
        variables: {
          jobsiteId,
          workerId,
          forceCreate: options?.forceCreate,
          workerCardInput: {
            cardNumber: badgeNumber?.trim(),
            workerCardFormatId: proximityCardFormatId.value,
            startDate: moment().toDate(),
          },
        },
      });

      const workerCardIdsToDeactivate = badges
        .filter(({ selected }) => selected)
        .map(({ workerCard }) => workerCard.workerCardId);

      if (workerCardIdsToDeactivate.length) {
        await removeCardFromWorker({
          variables: {
            workerCardIds: workerCardIdsToDeactivate,
          },
        });
      }
    };

    try {
      await addCardToWorkerAndRemoveExisting();
      AlertService.alert('success', 'Success', 'Badge assigned');
      onAction();
    } catch (error) {
      event.preventDefault();

      const workerCardAlreadyExistsError =
        getGraphQLErrorByCode<WorkerCardAlreadyExistsErrorExtensions>(
          error,
          AppErrorCode.WorkerCardAlreadyExists,
        );
      if (workerCardAlreadyExistsError) {
        const { scope, workerCardInfo, canBeReactivated } =
          workerCardAlreadyExistsError.extensions ?? {};

        if (scope === 'onDifferentWorker') {
          onAction({
            workerCardInfo,
            reassignBadge: async (): Promise<void> => {
              await addCardToWorkerAndRemoveExisting({ forceCreate: true });
            },
          });
        } else {
          const { workerCardId } = workerCardInfo?.[0] ?? {};
          formApi.setError('badgeNumber', {
            message: (
              <AlreadyExistingBadgeMessage
                canBeReactivated={canBeReactivated}
                errorMessage={workerCardAlreadyExistsError.message}
                onClick={(): void =>
                  reactivateBadge({
                    workerCardToReactivate: { id: workerCardId },
                    expirationDate: null,
                  })
                }
              />
            ),
            shouldFocus: true,
          });
        }
      } else {
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(error),
        );
      }
    } finally {
      if (isMounted()) {
        setIsFetching(false);
      }
    }
  };

  const {
    proximityCardFormatOptions,
    defaultProximityCardFormatOption,
    error: getJobsiteCardFormatsError,
  } = useProximityCardFormatOptions(jobsiteId);

  const formInputs = React.useMemo(
    () => getFormInputsHook({ proximityCardFormatOptions }),
    [proximityCardFormatOptions],
  );
  const defaultValues = React.useMemo(
    () =>
      getFormDefaultValues({
        workerCards,
        proximityCardFormatOptions,
        defaultProximityCardFormatOption,
      }),
    // remove workerCards from dependency so that `defaultValues` won't get recalculated
    // when `workerCards` are re-fetched after adding/removing cards
    [proximityCardFormatOptions, defaultProximityCardFormatOption],
  );

  return (
    <ModalFormContent
      setOpen={setOpen}
      renderAbove={<p className="-odin-mt-3 odin-mb-3">{instructions}</p>}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Register badge"
      actionIcon={CheckIcon}
      actionButtonWithSpinner={isFetching}
      error={getJobsiteCardFormatsError}
    />
  );
}
